import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button, Switch } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import { sampleService, sampleErrorMessage } from '../../Services/SampleService';
import '../../UI/Common.css';
import './Samples.css';

const emptyForm = { name: '', defaultContainer: '', isActive: true };

// Manage the lab's sample types. Types are deactivated, never deleted, so
// samples already recorded keep their type.
export default function SampleTypeSettings() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const fetchTypes = async () => {
    try {
      setLoading(true);
      setTypes(await sampleService.getTypes(true));
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to load sample types'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  const openAddModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (type) => {
    setEditingId(type.id);
    setFormData({ name: type.name, defaultContainer: type.defaultContainer || '', isActive: type.isActive });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) await sampleService.updateType(editingId, formData);
      else await sampleService.addType(formData);
      setShowModal(false);
      fetchTypes();
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to save sample type'));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (type) => {
    try {
      await sampleService.updateType(type.id, { ...type, isActive: !type.isActive });
      fetchTypes();
    } catch (error) {
      toast.error(sampleErrorMessage(error, 'Failed to update sample type'));
    }
  };

  return (
    <div className="container-fluid Container2 common-FontFamily">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="common-FontWeight m-0">Sample Types</h5>
        <Button variant="contained" size="small" startIcon={<AddIcon />} className="common-button" onClick={openAddModal}>
          Add Type
        </Button>
      </div>

      <div className="table-responsive common-box-shadow common-bg-white">
        <table className="table table-hover mb-0 common-FontSize-small align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th>Default Container</th>
              <th>Active</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="text-center py-3">Loading...</td></tr>
            ) : types.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-3 text-muted">No sample types yet.</td></tr>
            ) : (
              types.map((type) => (
                <tr key={type.id} style={{ opacity: type.isActive ? 1 : 0.6 }}>
                  <td>{type.name}</td>
                  <td>{type.defaultContainer || '-'}</td>
                  <td>
                    <Switch size="small" checked={type.isActive} onChange={() => toggleActive(type)} />
                  </td>
                  <td className="text-end">
                    <IconButton size="small" onClick={() => openEditModal(type)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth maxWidth="xs" className="common-modal">
        <DialogTitle className="d-flex justify-content-between align-items-center">
          {editingId ? 'Edit Sample Type' : 'Add Sample Type'}
          <IconButton onClick={() => setShowModal(false)} className="common-cross-button">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <div className="mb-3">
              <label className="common-Labels d-block">Name <span className="common-Asterisk">*</span></label>
              <input className="form-control common-Input" name="name" value={formData.name} onChange={handleChange} placeholder="e.g. Serum" required />
            </div>
            <div className="mb-2">
              <label className="common-Labels d-block">Default container</label>
              <input
                className="form-control common-Input"
                name="defaultContainer"
                value={formData.defaultContainer}
                onChange={handleChange}
                placeholder="e.g. Gel tube (Yellow)"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
